// ── FIELD MODE · HYBRID · OFFICE ────────────────────────────────────────────
//
// Which CHROME the app wears. Not which page you are on, not which data you
// can see, not which URL you are at.
//
// ⚠ THE RULE THAT MAKES THIS SAFE: MODE CHANGES THE CHROME ONLY, NEVER THE
// URL. The owner's decision, and it is the one that stops this becoming a
// second app. /jobs/abc is /jobs/abc on a phone, on a Fold and on a laptop —
// so a link anyone sends works everywhere, a bookmark survives a rotation, and
// nothing has to be kept in sync between two route tables. What changes is the
// bar at the bottom, which actions come first, and whether a heavy office
// screen renders itself or a summary of itself.
//
// ── BY WIDTH, WITH AN OVERRIDE ──────────────────────────────────────────────
// The owner's option (a), against the three tiers useDevice.js already has:
//
//   field    < 600px    a phone. Bottom bar, one column, big targets.
//   hybrid   600-1023   an unfolded Fold, an iPad in portrait, a small
//                       laptop window. Two panes, no bottom bar.
//   office   >= 1024    a laptop. The sidebar and everything it reaches.
//
// ⚠ WIDTH, NOT DEVICE. A folded Fold is 348px and an unfolded one is ~690px,
// on the same phone, mid-task. A width rule follows that fold; a user-agent
// rule cannot. It also means a narrow desktop window gets the phone layout,
// which is correct — the layout is about how much room there is.
//
// ⚠ AND AN OVERRIDE, BECAUSE 600 IS A GUESS ABOUT A PERSON. Somebody on a big
// phone may want the office layout; somebody on a small laptop may want Field
// Mode because they are in a truck. The override is per device (localStorage),
// because that is what it is about.
//
// Pure: no React. useLayoutMode() is the hook over it.

export const FIELD = 'field';
export const HYBRID = 'hybrid';
export const OFFICE = 'office';
export const AUTO = 'auto';

export const MODES = [FIELD, HYBRID, OFFICE];
export const MODE_CHOICES = [
  { key: AUTO, label: 'Automatic', hint: 'Phone, tablet or computer — decided by how wide the screen is.' },
  { key: FIELD, label: 'Field Mode', hint: 'The phone layout, on every screen. Big targets, one column, bottom bar.' },
  { key: HYBRID, label: 'Two-pane', hint: 'The tablet layout — a list beside what you picked.' },
  { key: OFFICE, label: 'Office', hint: 'The full sidebar layout, even on a phone. Expect to pinch and scroll.' },
];

const KEY = 'roofbeam.layoutMode';

// 'field' | 'hybrid' | 'office', from a width alone.
export function modeForWidth(width) {
  const w = Number(width);
  if (!Number.isFinite(w) || w <= 0) return OFFICE;   // unknown: the old behaviour
  if (w < 600) return FIELD;
  if (w < 1024) return HYBRID;
  return OFFICE;
}

// What the person chose, or 'auto'. ⚠ An unreadable store is 'auto', never a
// stuck mode — a phone in private browsing must not be trapped in Office.
export function storedOverride() {
  try {
    const v = window.localStorage.getItem(KEY);
    return MODES.includes(v) ? v : AUTO;
  } catch (_) {
    return AUTO;
  }
}

export function setOverride(mode) {
  try {
    if (!mode || mode === AUTO) window.localStorage.removeItem(KEY);
    else if (MODES.includes(mode)) window.localStorage.setItem(KEY, mode);
    else return false;
    return true;
  } catch (_) {
    return false;
  }
}

// The mode actually in force.
export function resolveMode({ width, override } = {}) {
  const o = override === undefined ? storedOverride() : override;
  if (MODES.includes(o)) return o;
  return modeForWidth(width);
}

// ── ⚠ WHICH SCREENS ARE TOO HEAVY FOR A PHONE ───────────────────────────────
// The owner's list, verbatim: "full takeoff, estimator, permits detail, report
// builder". On a phone these render a read-only SUMMARY and a line saying
// where to open the real thing.
//
// ⚠ A SUMMARY, NOT A BLOCK. "Not available on mobile" is the thing that makes
// somebody drive back to the office. The numbers that matter are shown; only
// the EDITING is withheld, and the screen says which device to use. Every one
// of these is a multi-column grid of number inputs that cannot be used at
// 390px without pinching — that is the test, not a general distaste for
// mobile.
export const HEAVY_ROUTES = [
  { match: /^\/takeoff/, what: 'the full takeoff', why: 'It is a wide grid of quantities and overrides.' },
  { match: /^\/estimator/, what: 'the estimator', why: 'Side-by-side pricing columns that need the width.' },
  { match: /^\/permits\/[^/]+/, what: 'the permit detail', why: 'Long forms with per-county fields.' },
  { match: /^\/reports?\/builder/, what: 'the report builder', why: 'Drag-and-drop sections need a pointer.' },
  { match: /^\/commercial/, what: 'the commercial takeoff', why: 'Wide tables of roof sections.' },
  { match: /^\/catalog/, what: 'the catalogue editor', why: 'A dense price table — read it here, edit it there.' },
];

export function heavyRoute(pathname) {
  const p = String(pathname || '');
  return HEAVY_ROUTES.find(r => r.match.test(p)) || null;
}

// ── THE PHONE'S BOTTOM BAR ──────────────────────────────────────────────────
// ⚠ FIVE SLOTS, AND THE MIDDLE ONE IS AN ACTION. The owner's decision:
// Today · Jobs · ➕ · Schedule · More. Leads, Storm and Dashboard move into
// More — they are reading screens, and a phone in a driveway is for doing.
//
// ⚠ TODAY IS FIRST AND IS THE HOME. Opening the app in a driveway should
// answer "what am I doing now" before it asks anything.
export const PHONE_TABS = [
  { path: '/today', label: 'Today', icon: '📍' },
  { path: '/jobs', label: 'Jobs', icon: '🔨' },
  { action: 'add', label: 'Add', icon: '➕' },
  { path: '/schedule', label: 'Schedule', icon: '🗓' },
  { action: 'more', label: 'More', icon: '☰' },
];

// What ➕ offers. Everything a phone is actually good for starting.
export const QUICK_ADD = [
  { path: '/jobs/new', label: 'New job', icon: '🏠' },
  { path: '/leads/new', label: 'New lead', icon: '🎯' },
  { path: '/canvassing', label: 'Knock a door', icon: '🚪' },
  { path: '/messages', label: 'Message', icon: '💬' },
  { path: '/todos', label: 'To-do', icon: '☑️' },
];

// ⚠ 44px, FROM APPLE'S OWN MINIMUM, and the owner asked for it by number.
// Exported so no screen has to remember it and no screen can quietly use 36.
export const TAP = 44;
